import { useState, useEffect, useCallback, useRef } from 'react';
import { sync } from '../lib/api';
import {
  db,
  getPendingMutations,
  removeMutations,
  getSyncState,
  updateSyncState,
} from '../lib/db';
import { useOnline } from './useOnline';
import type { SyncChange } from '../types';

const SYNC_INTERVAL = 30000; // 30 seconds

interface UseSyncResult {
  isSyncing: boolean;
  lastSyncAt: number | null;
  syncNow: () => Promise<void>;
  error: string | null;
}

/**
 * Hook for background sync with the server
 */
export function useSync(eventId: string | null): UseSyncResult {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isOnline = useOnline();
  const syncIntervalRef = useRef<number | null>(null);

  const syncNow = useCallback(async () => {
    if (!eventId || !isOnline) return;

    setIsSyncing(true);
    setError(null);

    try {
      // Get current sync state
      const syncState = await getSyncState(eventId);
      const sinceSeq = syncState?.lastSeq ?? 0;

      // Get pending mutations
      const mutations = await getPendingMutations();

      // Call sync API
      const response = await sync(eventId, sinceSeq, mutations);

      // Remove successfully processed mutations
      const successIds = response.results
        .filter((r) => r.status === 'ok' || r.status === 'duplicate')
        .map((r) => r.clientMutationId);
      
      if (successIds.length > 0) {
        await removeMutations(successIds);
      }

      // Apply incoming changes to local database
      await applyChanges(eventId, response.changes);

      // Update sync state
      await updateSyncState(eventId, response.newSeq);
      setLastSyncAt(Date.now());

      // Log any errors
      if (response.errors.length > 0) {
        console.error('Sync errors:', response.errors);
        setError(`${response.errors.length} mutation(s) failed`);
      }
    } catch (err) {
      console.error('Sync failed:', err);
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  }, [eventId, isOnline]);

  // Set up periodic sync
  useEffect(() => {
    if (!eventId || !isOnline) {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      return;
    }

    // Initial sync
    syncNow();

    // Set up interval
    syncIntervalRef.current = window.setInterval(syncNow, SYNC_INTERVAL);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [eventId, isOnline, syncNow]);

  return { isSyncing, lastSyncAt, syncNow, error };
}

/**
 * Apply sync changes to the local database
 */
async function applyChanges(eventId: string, changes: SyncChange[]): Promise<void> {
  for (const change of changes) {
    const { entityType, entityId, op, payload } = change;

    try {
      switch (entityType) {
        case 'room':
          if (op === 'delete') {
            await db.rooms.delete(entityId);
          } else if (payload) {
            await db.rooms.put({ ...(payload as Record<string, unknown>), id: entityId, eventId } as Parameters<typeof db.rooms.put>[0]);
          }
          break;

        case 'timeslot':
          if (op === 'delete') {
            await db.timeslots.delete(entityId);
          } else if (payload) {
            await db.timeslots.put({ ...(payload as Record<string, unknown>), id: entityId, eventId } as Parameters<typeof db.timeslots.put>[0]);
          }
          break;

        case 'proposal':
          if (op === 'delete') {
            await db.proposals.delete(entityId);
          } else if (payload) {
            await db.proposals.put({ ...(payload as Record<string, unknown>), id: entityId, eventId } as Parameters<typeof db.proposals.put>[0]);
          }
          break;

        case 'session':
          if (op === 'delete') {
            await db.sessions.delete(entityId);
          } else if (payload) {
            await db.sessions.put({ ...(payload as Record<string, unknown>), id: entityId, eventId } as Parameters<typeof db.sessions.put>[0]);
          }
          break;

        case 'vote':
          if (op === 'delete') {
            await db.votes.delete(entityId);
          } else if (payload) {
            await db.votes.put({ ...(payload as Record<string, unknown>), id: entityId, eventId } as Parameters<typeof db.votes.put>[0]);
          }
          break;

        case 'schedule_item':
          if (op === 'delete') {
            await db.scheduleItems.delete(entityId);
          } else if (payload) {
            await db.scheduleItems.put({ ...(payload as Record<string, unknown>), id: entityId, eventId } as Parameters<typeof db.scheduleItems.put>[0]);
          }
          break;
      }
    } catch (err) {
      console.error(`Failed to apply change for ${entityType}:${entityId}:`, err);
    }
  }
}
